import {
    WorkflowStepDataContentPack,
    WorkflowWorkflowInfo,
} from '@calm/evexia-web-sdk';

export const FtueStoredStepName = 'CURRENT_FTUE_WORKFLOW_STEP';
export const FtueStoredWorkflowDataName = 'CURRENT_FTUE_WORKFLOW_DATA';
export const FtueStoredCheckinTypeName = 'CURRENT_FTUE_CHECKIN_TYPE';
export const STORED_RETAKING_SURVEY_KEY = 'isRetakingSurvey';
export const STORED_CONVERSATION_ID_KEY = 'concierge_conversation_id';

export function clearFtueSessionStorage() {
    sessionStorage.removeItem(FtueStoredStepName);
    sessionStorage.removeItem(FtueStoredWorkflowDataName);
    sessionStorage.removeItem(FtueStoredCheckinTypeName);
}

export const StepStates = {
    IN_PROGRESS: 'step_in_progress',
    COMPLETED: 'step_completed',
    SKIPPED: 'step_skipped',
    DECLINED: 'step_declined',
} as const;

export type StepState = (typeof StepStates)[keyof typeof StepStates];

export const WorkflowIds = {
    FTUE: 'workflow-user-registration-ftue',
    FTUE_NO_VIDEO: 'workflow-user-registration-ftue-no-video',
    CONCIERGE_FTUE: 'workflow-user-registration-ai-concierge',
    CONCIERGE_GA_FTUE: 'workflow-user-registration-ai-concierge-ga',
    JOURNALING: 'workflow-journaling-mood-spectrum',
};

export const enum FtueStepDefinitionIds {
    ONBOARDING_VIDEO_1 = 'pack-class-goals-and-topics', // Goals and Topics intro video
    GOALS = 'step-checkin-entry-goal', // Goal selection
    TOPICS = 'step-checkin-entry-topic', // Topic selection
    ONBOARDING_VIDEO_2 = 'pack-class-goals-and-topics-pt-2', // Conditions intro video
    CONDITIONS = 'step-checkin-conditions', // Conditions selection
    ONBOARDING_VIDEO_3 = 'pack-class-mental-health-screen', // MHS intro video
    MENTAL_HEALTH_SCREENING = 'poll-mental-health', // MHS
    ONBOARDING_VIDEO_4 = 'pack-class-care-plan', // Care plan video
    NEW_ONBOARDING_VIDEO_4 = 'pack-class-non-concierge-care-plan-intro', // New care plan video
    END_WORKFLOW = 'step-end-workflow', // End (redirect to care plan)
}

export const ConciergeFtueStepDefinitionIds = {
    OVERVIEW_INTRO: 'step-overview-ai-concierge-intro', // Intro overview screen
    ONBOARDING_VIDEO_1: 'pack-class-mental-health-screen', // MHS intro video
    MENTAL_HEALTH_SCREENING: 'poll-mental-health', // MHS
    ONBOARDING_VIDEO_2: 'pack-class-mental-health-screen-pt-2', // MHS outro and results
    INITIAL_SELECTION: 'step-checkin-entry-ai-concierge-goal', // Initial goal selection
    REFINEMENT_1: 'step-checkin-entry-ai-concierge-refinement-pt-1', // Goal refinement #1
    REFINEMENT_2: 'step-checkin-entry-ai-concierge-refinement-pt-2', // Goal refinement #2
    FREE_TEXT: 'step-checkin-entry-ai-concierge-free-text', // Free text goal entry
    ECHO_BACK: 'step-checkin-entry-ai-concierge-echo-back', // Echo back
    ONBOARDING_VIDEO_3: 'pack-class-care-plan', // Care plan video
    NEW_ONBOARDING_VIDEO_3: 'pack-class-concierge-care-plan-intro', // New care plan video
    END_WORKFLOW: 'step-end-workflow', // End (redirect to care plan)
};

export const ConciergeGAFtueStepDefinitionIds = {
    OVERVIEW_INTRO: 'step-overview-ai-concierge-intro', // Intro overview screen
    ONBOARDING_VIDEO_1: 'pack-class-mental-health-screen', // MHS intro video
    MENTAL_HEALTH_SCREENING: 'poll-mental-health', // MHS
    ONBOARDING_VIDEO_2: 'pack-class-mental-health-screen-pt-2', // MHS outro and results
    GUIDE_MENU: 'ai-concierge-guide', // Guide menu
    INITIAL_SELECTION: 'step-checkin-entry-ai-concierge-guide', // Initial goal or topic selection
    REFINEMENT_1: 'step-checkin-entry-ai-concierge-refinement-pt-1', // Goal/Topic refinement #1
    REFINEMENT_2: 'step-checkin-entry-ai-concierge-refinement-pt-2', // Goal refinement #2
    FREE_TEXT: 'step-checkin-entry-ai-concierge-free-text', // Free text goal/topic entry
    ECHO_BACK: 'step-checkin-entry-ai-concierge-echo-back', // Echo back
    END_WORKFLOW: 'step-end-workflow', // End (redirect to care plan)
};

export const JournalingStepDefinitionIds = {
    INTRO: 'journaling-intro',
    STRESS_PROMPT: 'journaling-prompt-stressed',
    STRESS_PROMPT_RESPONSE: 'journaling-prompt-stressed-response',
    WORST_PROMPT: 'journaling-prompt-worst',
    WORST_PROMPT_RESPONSE: 'journaling-prompt-worst-response',
    BEST_PROMPT: 'journaling-prompt-best',
    BEST_PROMPT_RESPONSE: 'journaling-prompt-best-response',
    REALISTIC_PROMPT: 'journaling-prompt-realistic',
    REALISTIC_PROMPT_RESPONSE: 'journaling-prompt-realistic-response',
    REFLECTION_INTRO: 'journaling-reflection-intro',
    EMOTION_PROMPT: 'journaling-prompt-feeling-now',
    EMOTION_PROMPT_RESPONSE: 'journaling-prompt-feeling-now-response',
    END_WORKFLOW: 'step-end-workflow',
};

export const JOURNALING_PROMPT_RESPONSE_STEP: Record<string, string> = {
    [JournalingStepDefinitionIds.STRESS_PROMPT]:
        JournalingStepDefinitionIds.STRESS_PROMPT_RESPONSE,
    [JournalingStepDefinitionIds.WORST_PROMPT]:
        JournalingStepDefinitionIds.WORST_PROMPT_RESPONSE,
    [JournalingStepDefinitionIds.BEST_PROMPT]:
        JournalingStepDefinitionIds.BEST_PROMPT_RESPONSE,
    [JournalingStepDefinitionIds.REALISTIC_PROMPT]:
        JournalingStepDefinitionIds.REALISTIC_PROMPT_RESPONSE,
    [JournalingStepDefinitionIds.EMOTION_PROMPT]:
        JournalingStepDefinitionIds.EMOTION_PROMPT_RESPONSE,
};

/**
 * Reverse lookup for {@link JOURNALING_PROMPT_RESPONSE_STEP}: given a response
 * step, return the prompt step that maps to it. Response steps are
 * client-injected (they have no backend step of their own), so several places
 * need to resolve the originating prompt from the response — e.g. to attribute
 * echo-back analytics to the prompt they answer, or to complete the backend
 * workflow (which is still parked on the prompt) from the echo back.
 */
export function getJournalingPromptStepForResponse(
    responseStep: string | null | undefined
): string | undefined {
    if (!responseStep) return undefined;
    return Object.keys(JOURNALING_PROMPT_RESPONSE_STEP).find(
        (promptStep) =>
            JOURNALING_PROMPT_RESPONSE_STEP[promptStep] === responseStep
    );
}

export function getWorkflowPackClass(workflow: WorkflowWorkflowInfo) {
    if (workflow.packClass) return workflow.packClass;
    const data: WorkflowStepDataContentPack | undefined = workflow?.steps.find(
        (step) =>
            step.stepDefn.type === 'content_pack' && step.stepDefn.data.display
    )?.stepDefn.data as WorkflowStepDataContentPack;
    return data?.relatedId;
}

export function getWorkflowCurrentStep(
    workflow: WorkflowWorkflowInfo | undefined
) {
    return workflow?.steps.find(
        (step) => step?.stepInstance?.currentState === StepStates.IN_PROGRESS
    );
}

/**
 * Caveat: this assumes the number of steps in the workflow is constant.
 * The workflow service can dynamically skip steps based on prior answers
 * (e.g. FTUE skips later mental-health questions when GAD2/PHQ2 are
 * answered "Not At All"), so the denominator here may exceed the number
 * of steps a given user actually encounters.
 */
export function calculateWorkflowProgress(
    workflow: WorkflowWorkflowInfo | undefined
): number {
    if (!workflow?.steps?.length) return 0;
    const finishedCount = workflow.steps.filter(
        (step) =>
            step.stepInstance?.currentState === StepStates.COMPLETED ||
            step.stepInstance?.currentState === StepStates.SKIPPED
    ).length;
    return Math.round((finishedCount / workflow.steps.length) * 100);
}

export function getWorkflowByPackClass(
    packClassId: string,
    workflows?: WorkflowWorkflowInfo[]
) {
    return workflows?.find(
        (workflow) => getWorkflowPackClass(workflow) === packClassId
    );
}
